import { useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Alert, Button, SectionCard } from "@/deps/ui-library";
import {
  useCompanyProfile,
  useConvertQuotation,
  useDuplicateQuotation,
  useNumberQuotation,
  useQuotation,
  useRespondToQuotation,
  useSendQuotation,
} from "@/api/queries";
import { useCan, useDocumentMailEnabled, useTaxWords } from "@/app/session";
import { useToast } from "@/app/toasts";
import { Dialog } from "@/components/Dialog";
import { DocumentScreen, LinesTable } from "@/components/DocumentScreen";
import { DocumentEmails } from "@/components/DocumentEmails";
import { DownloadPdfButton } from "@/components/DownloadPdfButton";
import { DraftActions } from "@/components/DraftActions";
import { PrintOverlay } from "@/components/print/PrintOverlay";
import { QuotationPrint } from "@/components/print/QuotationPrint";
import { SendEmailDialog } from "@/components/SendEmailDialog";
import { TotalsBlock, VatBreakdownTable } from "@/components/TotalsBlock";
import { calculateTotals } from "@/lib/invoiceTotals";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { QuotationStatusBadge } from "./QuotationStatusBadge";

const WHY_LOCKED = {
  converted: "It's kept as the record of what was agreed.",
  declined: "Declined by the customer. Duplicate it to quote again.",
  expired: "The validity date has passed. Duplicate it to quote again with fresh dates.",
} as const;

export function QuotationDetailPage() {
  const say = useTaxWords();
  const { id } = useParams({ from: "/sales/quotations/$id" });
  const navigate = useNavigate();
  const { data, isPending, error } = useQuotation(id);
  const { data: seller } = useCompanyProfile();
  const send = useSendQuotation();
  const number = useNumberQuotation();
  const mailEnabled = useDocumentMailEnabled();
  const toast = useToast();
  const [emailing, setEmailing] = useState(false);
  const respond = useRespondToQuotation();
  const duplicate = useDuplicateQuotation();
  const convert = useConvertQuotation();
  const can = useCan();
  const [printing, setPrinting] = useState(false);
  const [converting, setConverting] = useState(false);

  if (isPending) return <p className="text-fg-muted p-6 text-sm">Loading quotation…</p>;
  if (error || !data)
    return <p className="text-bad-text p-6 text-sm">That quotation could not be loaded.</p>;

  const mayWrite = can("sales.quotations.create");
  const totals = calculateTotals(
    data.lines.map((line) => ({
      category: line.vatCategory,
      rate: line.vatRatePct,
      grossPrice: line.unitPrice,
      quantity: line.quantity,
      charges: line.charges,
      allowances: line.allowances,
    })),
    data.adjustments,
  );
  const failure = number.error ?? send.error ?? respond.error ?? duplicate.error ?? convert.error;
  const whyLocked =
    data.status === "converted" || data.status === "declined" || data.status === "expired"
      ? WHY_LOCKED[data.status]
      : null;

  const copy = () =>
    duplicate.mutate(id, {
      onSuccess: (copied) => void navigate({ to: "/sales/quotations/$id", params: { id: copied } }),
    });

  return (
    <>
      <PrintOverlay
        open={printing}
        onClose={() => setPrinting(false)}
        title={`Quotation ${data.number ?? ""}`.trim()}
      >
        <QuotationPrint quotation={data} seller={seller} />
      </PrintOverlay>

      {emailing ? (
        <SendEmailDialog
          target={{ kind: "quotation", id }}
          onClose={() => setEmailing(false)}
          onSent={(recipients) => {
            setEmailing(false);
            toast({
              title: `${data.number ?? "The quotation"} is on its way to ${recipients.join(", ")}.`,
              scheme: "success",
            });
          }}
        />
      ) : null}

      <Dialog
        open={converting}
        onClose={() => setConverting(false)}
        title={`Convert ${data.number ?? "this quotation"} to an invoice`}
        footer={
          <>
            <Button
              colorScheme="default"
              variant="ghost"
              size="sm"
              onClick={() => setConverting(false)}
            >
              Cancel
            </Button>
            <Button
              colorScheme="primary"
              variant="solid"
              size="sm"
              disabled={convert.isPending}
              onClick={() =>
                convert.mutate(id, {
                  onSuccess: (invoice) =>
                    void navigate({ to: "/sales/invoices/$id", params: { id: invoice } }),
                  onSettled: () => setConverting(false),
                })
              }
            >
              {convert.isPending ? "Creating" : "Create the invoice"}
            </Button>
          </>
        }
      >
        <p className="text-fg text-sm">
          This makes a draft invoice to {data.customerName} for{" "}
          <span className="num">{formatAmount(data.total)}</span>, dated today, with these lines.
          Nothing is in the ledger until the invoice is issued.
        </p>
      </Dialog>

      <DocumentScreen
        title="Quotation"
        number={data.number}
        badge={<QuotationStatusBadge status={data.status} />}
        subtitle={`${data.customerName} · valid until ${formatDate(data.validUntil)}`}
        actions={
          <span className="flex items-center gap-2">
            {data.status === "draft" && mayWrite ? (
              <DraftActions
                kind="quotations"
                label="quotation"
                id={id}
                onEdit={() => void navigate({ to: "/sales/quotations/$id/edit", params: { id } })}
                onDeleted={() => void navigate({ to: "/sales/quotations" })}
              />
            ) : null}
            {data.status !== "draft" && mayWrite ? (
              <Button
                colorScheme="default"
                variant="outline"
                size="sm"
                disabled={duplicate.isPending}
                onClick={copy}
              >
                Duplicate
              </Button>
            ) : null}
            {data.number ? (
              <DownloadPdfButton kind="quotation" id={id} number={data.number} />
            ) : null}
            <Button
              colorScheme="default"
              variant="outline"
              size="sm"
              onClick={() => setPrinting(true)}
            >
              Print
            </Button>
            {(data.status === "sent" || data.status === "accepted") && mailEnabled && mayWrite ? (
              <Button
                colorScheme="default"
                variant="outline"
                size="sm"
                onClick={() => setEmailing(true)}
              >
                Email quotation
              </Button>
            ) : null}
            {data.status === "draft" && mayWrite ? (
              <Button
                colorScheme={mailEnabled ? "default" : "primary"}
                variant={mailEnabled ? "outline" : "solid"}
                size="sm"
                disabled={send.isPending}
                onClick={() => send.mutate(id)}
              >
                {send.isPending ? "Saving" : "Mark as sent"}
              </Button>
            ) : null}
            {data.status === "draft" && mayWrite && mailEnabled ? (
              <Button
                colorScheme="primary"
                variant="solid"
                size="sm"
                disabled={number.isPending}
                onClick={() => number.mutate(id, { onSuccess: () => setEmailing(true) })}
              >
                Send to customer
              </Button>
            ) : null}
            {data.status === "sent" && mayWrite ? (
              <>
                <Button
                  colorScheme="default"
                  variant="outline"
                  size="sm"
                  disabled={respond.isPending}
                  onClick={() => respond.mutate({ id, accepted: false })}
                >
                  Mark declined
                </Button>
                <Button
                  colorScheme="primary"
                  variant="solid"
                  size="sm"
                  disabled={respond.isPending}
                  onClick={() => respond.mutate({ id, accepted: true })}
                >
                  Mark accepted
                </Button>
              </>
            ) : null}
            {data.status === "accepted" && mayWrite && can("sales.invoices.create") ? (
              <Button
                colorScheme="primary"
                variant="solid"
                size="sm"
                onClick={() => setConverting(true)}
              >
                Convert to invoice
              </Button>
            ) : null}
          </span>
        }
        facts={[
          { label: "Customer", value: data.customerName },
          { label: "Quotation date", value: formatDate(data.date) },
          { label: "Valid until", value: formatDate(data.validUntil) },
          ...(data.reference ? [{ label: "Reference", value: data.reference }] : []),
          ...(data.sentAt ? [{ label: "Sent", value: formatDate(data.sentAt.slice(0, 10)) }] : []),
          ...(data.invoiceId
            ? [
                {
                  label: "Converted to",
                  value: (
                    <Link
                      to="/sales/invoices/$id"
                      params={{ id: data.invoiceId }}
                      className="num text-primary underline"
                    >
                      {data.invoiceNumber ?? "Draft invoice"}
                    </Link>
                  ),
                },
              ]
            : []),
          { label: "Amount", value: <span className="num">{formatAmount(data.total)}</span> },
        ]}
        history={data.history}
        aside={<DocumentEmails of={{ kind: "quotation", id }} />}
      >
        {failure ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(failure as Error).message}
          />
        ) : null}

        {whyLocked ? (
          <Alert config={{ variant: "subtle", colorScheme: "info" }} description={whyLocked} />
        ) : null}

        <SectionCard title="Lines" flush>
          <LinesTable
            caption={`Lines of quotation ${data.number ?? "draft"}`}
            lines={data.lines.map((line) => ({
              n: line.n,
              label: line.itemName,
              account: line.accountName,
              quantity: line.quantity,
              unitPrice: formatAmount(line.unitPrice),
              tax: `${line.vatCategory} · ${line.vatRatePct}%`,
              net: formatAmount(line.lineNet),
            }))}
          />
        </SectionCard>

        <div className="grid gap-4 md:grid-cols-2">
          <SectionCard title={say("VAT breakdown")}>
            <VatBreakdownTable totals={totals} />
          </SectionCard>
          <SectionCard title="Totals">
            <TotalsBlock totals={totals} showDue={false} />
          </SectionCard>
        </div>

        {data.terms || data.note ? (
          <SectionCard title="Terms and note">
            <div className="flex flex-col gap-2 text-sm">
              {data.terms ? <p className="text-fg whitespace-pre-line">{data.terms}</p> : null}
              {data.note ? <p className="text-fg-muted whitespace-pre-line">{data.note}</p> : null}
            </div>
          </SectionCard>
        ) : null}
      </DocumentScreen>
    </>
  );
}
